/*
  Theme JS — vanilla, IIFE pattern.
  Scope:
  - toggleData(el, key, force) helper
  - Nav open/close (single state on .c-nav, mobile only)
  - Activity-filters expand/collapse (data-expanded on section root)
*/

(function () {
  'use strict';

  function toggleData(el, key, force) {
    const current = el.dataset[key] === 'true';
    const next = force === undefined ? !current : force;
    el.dataset[key] = next ? 'true' : 'false';
    return next;
  }

  function initNav() {
    const nav = document.querySelector('.c-nav');
    if (!nav) return;

    const toggle = nav.querySelector('.c-nav__toggle');
    const backdrop = nav.querySelector('.c-nav__backdrop');
    if (!toggle || !backdrop) return;

    const closeBtn = nav.querySelector('.c-nav__close');
    const closeToggle = closeBtn ? closeBtn.querySelector('.c-expand-toggle') : null;

    function setOpen(open) {
      toggleData(nav, 'open', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-hidden', String(open));
      if (open) {
        toggle.setAttribute('tabindex', '-1');
      } else {
        toggle.removeAttribute('tabindex');
      }
      if (closeToggle) closeToggle.setAttribute('data-expanded', String(!open));
    }

    toggle.addEventListener('click', function (event) {
      event.stopPropagation();
      setOpen(nav.dataset.open !== 'true');
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', function (event) {
        event.stopPropagation();
        setOpen(false);
      });
    }

    backdrop.addEventListener('click', function () {
      setOpen(false);
    });

    /* Clicks on the .c-header::after scrim (the full-viewport blur) route
       to .c-header as the closest DOM ancestor of the pseudo. Descendant
       clicks (logo, search, menu links, toggle) have their own targets and
       are filtered out by the target check. Scoped to the header element
       so we don't run on every document click. */
    const header = nav.closest('.c-header');
    if (header) {
      header.addEventListener('click', function (event) {
        if (event.target !== header) return;
        if (nav.dataset.open !== 'true') return;
        setOpen(false);
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.dataset.open === 'true') {
        setOpen(false);
      }
    });
  }

  /**
   * revealOnScroll(selector, options)
   *
   * Sets data-revealed="true" on the first element matching `selector`
   * when it intersects the viewport. Fires once, then disconnects.
   *
   * @param {string} selector  CSS selector for the element to observe
   * @param {object} [options]
   * @param {number} [options.threshold=0.1]   IntersectionObserver threshold
   * @param {string} [options.rootMargin='0px'] IntersectionObserver rootMargin
   *        (e.g. '-50% 0px -50% 0px' fires when the element crosses the
   *        viewport's vertical center)
   */
  function revealOnScroll(selector, options) {
    const opts = options || {};
    const threshold = opts.threshold !== undefined ? opts.threshold : 0.1;
    const rootMargin = opts.rootMargin !== undefined ? opts.rootMargin : '0px';
    const el = document.querySelector(selector);
    if (!el) return;
    const observer = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        requestAnimationFrame(function () {
          el.dataset.revealed = 'true';
        });
        observer.disconnect();
      }
    }, { threshold: threshold, rootMargin: rootMargin });
    observer.observe(el);
  }

  /**
   * initSmoothScroll()
   *
   * Lerp-to-target smooth/inertia scrolling for the wheel (desktop/trackpad).
   * Sibling of point-focus's friction-decay inertia: there a flick's velocity
   * decays each frame; here `current` eases toward a wheel-driven `target`
   * every frame (current += (target - current) * LERP).
   *
   * Native scroll is preserved — we only ease window.scrollTo toward a target,
   * so the sticky header, position:fixed UI, background-attachment:fixed
   * parallax, anchor links, keyboard scroll, and the IntersectionObserver
   * reveals all keep working. Touch is left untouched (wheel doesn't fire on
   * touch scroll), so mobile keeps its native momentum.
   *
   * Bails to native scroll for: pinch-zoom (ctrlKey), an open scroll-locking
   * overlay, and any nested scroller that can still move in the wheel direction.
   */
  function initSmoothScroll() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.Shopify && window.Shopify.designMode) return; // don't fight the theme editor

    const LERP = 0.1; // 0..1 — higher = snappier, lower = floatier
    const STOP = 0.5; // px from target where we settle
    let target = window.scrollY;
    let rafId = null;

    function maxScroll() {
      return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    }

    function clamp(value) {
      return Math.max(0, Math.min(value, maxScroll()));
    }

    /* Walk up from the wheel target; if an ancestor actually scrolls in the
       wheel direction, return true so we leave it to native scroll (search
       dropdown, PLP filter rail, gallery thumb column, hero pill menu). */
    function hasScrollableAncestor(node, deltaY) {
      let el = node;
      while (el && el !== document.body && el !== document.documentElement) {
        if (el.nodeType === 1 && el.scrollHeight > el.clientHeight) {
          const overflowY = getComputedStyle(el).overflowY;
          if (overflowY === 'auto' || overflowY === 'scroll') {
            const canScrollUp = deltaY < 0 && el.scrollTop > 0;
            const canScrollDown =
              deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1;
            if (canScrollUp || canScrollDown) return true;
          }
        }
        el = el.parentNode;
      }
      return false;
    }

    function tick() {
      const current = window.scrollY; // re-read so keyboard/scrollbar/anchor resync for free
      const delta = target - current;
      if (Math.abs(delta) < STOP) {
        window.scrollTo(0, target);
        rafId = null;
        return;
      }
      window.scrollTo(0, current + delta * LERP);
      rafId = window.requestAnimationFrame(tick);
    }

    window.addEventListener(
      'wheel',
      function (event) {
        if (event.ctrlKey) return; // pinch-zoom — let the browser handle it
        if (maxScroll() === 0) return; // nothing to scroll
        if (document.querySelector('[data-locks-scroll][data-open="true"]')) return; // locked overlay
        if (hasScrollableAncestor(event.target, event.deltaY)) return; // nested scroller

        // Normalize delta: pixels (0), lines (1), pages (2).
        const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;

        // Idle -> resync target to wherever native scroll (keyboard, anchor,
        // scrollbar) left the page before we take over.
        if (rafId === null) target = window.scrollY;
        target = clamp(target + event.deltaY * unit);

        event.preventDefault(); // we own this gesture; suppress native wheel scroll
        if (rafId === null) rafId = window.requestAnimationFrame(tick);
      },
      { passive: false }
    );

    window.addEventListener('resize', function () {
      target = clamp(target);
    });
  }

  /**
   * initHeaderAutoHide()
   *
   * Desktop-only (>= 900px) Amazon-style header:
   *  - data-hidden : true while scrolling DOWN past the header, false on
   *    scroll UP or near the top. Drives transform: translateY(-100%).
   *  - data-scrolled : true once the first content section (hero) has
   *    scrolled out from under the header. Drives the solid-navy background.
   * Mobile keeps its own CSS scroll-fade behavior; this controller no-ops there.
   */
  function initHeaderAutoHide() {
    const header = document.querySelector('.c-header');
    if (!header) return;

    const desktop = window.matchMedia('(min-width: 900px)');
    const headerHeight = header.offsetHeight || 80;
    const DELTA = 6; // px of travel required before we react (anti-jitter)

    let lastY = window.scrollY;
    let ticking = false;

    function update() {
      ticking = false;

      if (!desktop.matches) {
        lastY = window.scrollY;
        return;
      }

      const y = window.scrollY;

      // Near the top: always show.
      if (y <= headerHeight) {
        toggleData(header, 'hidden', false);
        lastY = y;
        return;
      }

      const diff = y - lastY;
      if (Math.abs(diff) < DELTA) return; // ignore micro-scroll; lastY unchanged so it accumulates

      // Past the top zone: direction drives visibility. (Background is a
      // separate concern, driven by data-scrolled / the hero observer.)
      toggleData(header, 'hidden', diff > 0); // down -> hide, up -> show

      lastY = y;
    }

    function onScroll() {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }

    // data-scrolled: observe the first content section (hero) leaving the
    // viewport top. No hero on the page -> solid immediately.
    const hero = document.querySelector('[data-hero]') || document.querySelector('main > .shopify-section:first-child');
    if (hero) {
      const io = new IntersectionObserver(
        function (entries) {
          header.dataset.scrolled = entries[0].isIntersecting ? 'false' : 'true';
        },
        { rootMargin: '-' + headerHeight + 'px 0px 0px 0px', threshold: 0 }
      );
      io.observe(hero);
    } else {
      header.dataset.scrolled = 'true';
    }

    window.addEventListener('scroll', onScroll, { passive: true });

    // Keyboard safety net: reveal on focus (pairs with CSS :focus-within).
    header.addEventListener('focusin', function () {
      toggleData(header, 'hidden', false);
    });

    // Leaving desktop: clear hidden so mobile starts clean.
    desktop.addEventListener('change', function (e) {
      if (!e.matches) toggleData(header, 'hidden', false);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initNav();
    initSmoothScroll();
    initHeaderAutoHide();
  });

  window.toggleData = toggleData;
  window.revealOnScroll = revealOnScroll;
})();
